<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { MessageSchema } from '@/locales';
import { useUserStore } from '@/stores';
import { useAuthStore } from '@repo/vue/stores';
import { ResourceConfig } from '@repo/core/resources';

// locale
const { t } = useI18n<[MessageSchema]>();

// stores
const userStore = useUserStore();
const authStore = useAuthStore();

// state
const { currentUser } = storeToRefs(userStore);
const activationGuideUrl = 'https://draftworx.helpjuice.com/cloud/two-factor-authentication-2fa';

// computed
// The server decides whether 2FA is mandatory and returns twoFactorMandatory on the current user.
// That flag already folds in the effective exemption (user- or practice-level), so we gate purely
// on it. While mandatory-and-not-enabled the dialog is shown and cannot be dismissed; if an
// exemption later clears twoFactorMandatory (after currentUser is refreshed) the dialog reactively
// disappears without requiring a re-login.
const visible = computed(() => currentUser.value?.twoFactorMandatory === true && !currentUser.value?.twoFactorEnabled);

// methods
const setupTwoFactor = () => {
  location.href = `${ResourceConfig.authServer}/Manage/TwoFactorAuthentication?returnUrl=${encodeURIComponent(
    location.href
  )}`;
};
const logout = async () => {
  await authStore.logout();
};
</script>

<template>
  <Dialog
    :visible="visible"
    :modal="true"
    :closable="false"
    :close-on-escape="false"
    :draggable="false"
    :header="t('user.two_factor_required.header')"
    class="dialog-sm"
  >
    <div class="migration-info">
      <i class="fa fa-info-circle info-icon" aria-hidden="true"></i>
      <div class="migration-info-text">
        <p>{{ t('user.two_factor_required.message') }}</p>
        <p>{{ t('user.two_factor_required.continue_message') }}</p>
      </div>
    </div>
    <template #footer>
      <div class="tw-flex tw-justify-between tw-items-center tw-w-full">
        <!-- Left side: activation guide -->
        <a :href="activationGuideUrl" target="_blank" rel="noopener">{{
          t('user.two_factor_required.activation_guide')
        }}</a>
        <!-- Right side buttons -->
        <div class="tw-flex tw-justify-end tw-items-center tw-gap-2">
          <Button :label="t('user.two_factor_required.setup')" autofocus @click="setupTwoFactor" />
          <Button :label="t('user.two_factor_required.logout')" @click="logout" />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<style scoped lang="scss">
// Blue info box matching the web app's TwoFactorRequiredModal.
.migration-info {
  display: flex;
  align-items: flex-start;
  background: #ecf8ff;
  border: 1px solid #55b2e9;
  border-radius: 0.364rem; // 4px
  padding: 1.364rem; // 15px
  // .p-dialog-content sets color: var(--dx-gray) (#888888 in both themes), which reads washed out
  // against the callout fill. Opt back into the themed text colour so the copy is dark on the light
  // fill and light on the dark tint below.
  color: var(--text-color);

  .info-icon {
    color: #55b2e9;
    font-size: 2.182rem; // 24px
    margin-right: 1.364rem; // 15px
    margin-top: 0.182rem; // 2px
  }

  .migration-info-text p {
    margin: 0;
    line-height: 1.5;

    + p {
      margin-top: 0.909rem; // 10px
    }
  }
}

// Dark mode (.app-dark on <html>): the #55b2e9 border/icon is the app's --primary-color
// in both themes, so only the light-blue fill needs replacing; --text-color above flips the copy.
.app-dark .migration-info {
  background: rgba(85, 178, 233, 0.12);
}
</style>
